<script lang="ts">
  import { tick, createEventDispatcher } from 'svelte';
  import { toast } from '../js/toast.js';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { spinner } from '../js/spinner.js';
  import { loadStripeScript } from '../js/stripe-loader.js';
  import Modal from './Modal.svelte';

  export let displayCheckout = false;
  /** When provided, this is called with paymentMethodId instead of submitShoppingCart (e.g. for pack purchase). */
  export let onSubmitPaymentMethod: ((paymentMethodId: string) => Promise<{ success?: boolean; message?: string }>) | undefined = undefined;
  /** Optional summary label shown at top of checkout (e.g. "10 Briefs Pack") */
  export let summaryLabel: string = '';
  /** Optional amount shown at top of checkout (e.g. "$10.00") */
  export let summaryAmount: string = '';

  const dispatch = createEventDispatcher();

  let stripe: any;
  let elements: any;
  let cardNumber: any;
  let cardExpiry: any;
  let cardCvc: any;
  let cardholderName = '';
  let isPaying = false;
  let modalWidth = 460;
  let savedCards: { id: string; brand: string; last4: string; is_default?: boolean }[] = [];
  let selectedCardId: string | null = null;
  let useNewCard = false;
  let isLoadingCards = false;

  const stripePublishableKey = typeof import.meta.env.VITE_STRIPE_KEY === 'string' ? (import.meta.env.VITE_STRIPE_KEY || '').trim() : '';

  function ensureStripeLoaded() {
    if (stripe || !stripePublishableKey) return;
    loadStripeScript().then((StripeFn) => {
      if (StripeFn && !stripe) {
        stripe = StripeFn(stripePublishableKey);
        elements = stripe.elements();
      }
    });
  }

  $: if (displayCheckout) {
    ensureStripeLoaded();
    if (!isLoadingCards && savedCards.length === 0 && !selectedCardId && !useNewCard) {
      loadSavedCards();
    }
  }

  $: if (!displayCheckout) {
    savedCards = [];
    selectedCardId = null;
    useNewCard = false;
    cardholderName = '';
  }

  async function loadSavedCards() {
    if (!$auth.user?.uuid) return;
    isLoadingCards = true;
    try {
      const response = (await api.getUserCards($auth.user.uuid)) as { success?: boolean; data?: { id: string; brand: string; last4: string; is_default?: boolean }[] };
      if (response?.success && response.data) {
        savedCards = response.data;
        if (savedCards.length > 0) {
          const defaultCard = response.data.find((c) => c.is_default) || response.data[0];
          selectedCardId = defaultCard.id;
          useNewCard = false;
        } else {
          useNewCard = true;
          tick().then(() => setupCardElements());
        }
      } else {
        savedCards = [];
        useNewCard = true;
        tick().then(() => setupCardElements());
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      savedCards = [];
      useNewCard = true;
      tick().then(() => setupCardElements());
    } finally {
      isLoadingCards = false;
    }
  }

  function handleUseNewCard() {
    useNewCard = true;
    selectedCardId = null;
    tick().then(() => setupCardElements());
  }

  function setupCardElements() {
    if (cardNumber) {
      cardNumber.destroy();
      cardNumber = null;
    }
    if (cardExpiry) {
      cardExpiry.destroy();
      cardExpiry = null;
    }
    if (cardCvc) {
      cardCvc.destroy();
      cardCvc = null;
    }
    const numberEl = document.getElementById('checkout-card-number');
    const expiryEl = document.getElementById('checkout-card-expiry');
    const cvcEl = document.getElementById('checkout-card-cvc');
    if (!stripe || !elements || !numberEl || !expiryEl || !cvcEl) return;

    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Poppins", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: '#fa755a', iconColor: '#fa755a' },
    };
    cardNumber = elements.create('cardNumber', { style });
    cardNumber.mount('#checkout-card-number');
    cardExpiry = elements.create('cardExpiry', { style });
    cardExpiry.mount('#checkout-card-expiry');
    cardCvc = elements.create('cardCvc', { style });
    cardCvc.mount('#checkout-card-cvc');
  }

  function getCardBrandIcon(brand: string): string {
    const map: Record<string, string> = {
      visa: 'cc visa',
      mastercard: 'cc mastercard',
      amex: 'cc amex',
      discover: 'cc discover',
      diners: 'cc diners club',
      jcb: 'cc jcb',
    };
    return map[brand?.toLowerCase()] || 'credit card';
  }

  function capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  async function handleSubmit() {
    let paymentMethodId: string;

    if (useNewCard) {
      if (!stripe) {
        toast.showNotification(false, 'Payment is not configured. Please set VITE_STRIPE_KEY in your environment.');
        return;
      }
      if (!cardholderName?.trim()) {
        toast.showNotification(false, 'Please enter the name on card');
        return;
      }
      isPaying = true;
      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumber,
          billing_details: { name: cardholderName },
        });
        if (error) {
          toast.showNotification(false, error.message);
          isPaying = false;
          return;
        }
        paymentMethodId = paymentMethod.id;
      } catch (err) {
        toast.showNotification(false, 'Error processing card');
        isPaying = false;
        return;
      }
    } else {
      if (!selectedCardId) {
        toast.showNotification(false, 'Please select a payment method');
        return;
      }
      paymentMethodId = selectedCardId;
      isPaying = true;
    }

    try {
      let result: { success?: boolean; message?: string };
      if (onSubmitPaymentMethod) {
        result = (await onSubmitPaymentMethod(paymentMethodId)) || {};
      } else {
        spinner.show();
        const response = (await api.submitShoppingCart($auth.user.uuid, paymentMethodId, false)) as { success?: boolean; message?: string };
        spinner.hide();
        toast.showNotification(!!response.success, response.message || (response.success ? 'Payment successful' : 'Payment failed'));
        result = response;
      }
      if (result.success) {
        dispatch('paid', { paymentMethodId });
        displayCheckout = false;
      } else if (result.message && !onSubmitPaymentMethod) {
        toast.showNotification(false, result.message);
      }
    } catch (err) {
      toast.showNotification(false, 'Error processing payment');
    } finally {
      isPaying = false;
    }
  }

  function handleClose() {
    displayCheckout = false;
  }
</script>

<Modal bind:showModal={displayCheckout} bind:modalWidth>
  <div slot="header">Payment</div>
  <div slot="body">
    {#if summaryLabel || summaryAmount}
      <div class="order-summary">
        <span class="order-summary-label">{summaryLabel}</span>
        <span class="order-summary-amount">{summaryAmount}</span>
      </div>
    {/if}
    <div class="payment-section">
      <h4 class="section-title">Payment Method</h4>

      {#if isLoadingCards}
        <div class="cards-loading">
          <i class="spinner loading icon"></i>
          <span>Loading payment methods...</span>
        </div>
      {:else}
        {#if savedCards.length > 0}
          <div class="payment-options">
            {#each savedCards as card}
              <label class="payment-option" class:selected={!useNewCard && selectedCardId === card.id}>
                <input
                  type="radio"
                  name="payment-method"
                  value={card.id}
                  checked={!useNewCard && selectedCardId === card.id}
                  on:change={() => { selectedCardId = card.id; useNewCard = false; }}
                />
                <div class="option-content">
                  <i class="{getCardBrandIcon(card.brand)} icon card-brand-icon"></i>
                  <span class="card-info">{capitalizeFirst(card.brand)} •••• {card.last4}</span>
                </div>
              </label>
            {/each}
            <label class="payment-option" class:selected={useNewCard}>
              <input
                type="radio"
                name="payment-method"
                value="new"
                checked={useNewCard}
                on:change={handleUseNewCard}
              />
              <div class="option-content">
                <i class="plus icon"></i>
                <span>Add new card</span>
              </div>
            </label>
          </div>
        {/if}

        {#if useNewCard}
          {#if !stripePublishableKey}
            <p class="stripe-not-configured">Payment is not configured. Please set VITE_STRIPE_KEY in your environment.</p>
          {:else}
          <div class="new-card-form" class:has-saved-cards={savedCards.length > 0}>
            <div class="checkout-div">
              <label for="checkout-cardholder-name" class="checkout-label">Name on Card</label>
              <input
                id="checkout-cardholder-name"
                class="checkout-input"
                type="text"
                bind:value={cardholderName}
                placeholder="John Smith"
                required
              />
            </div>
            <div class="checkout-div">
              <span class="checkout-label" id="checkout-card-number-label">Card Number</span>
              <div id="checkout-card-number" class="checkout-stripe" aria-labelledby="checkout-card-number-label"></div>
            </div>
            <div class="checkout-row">
              <div class="checkout-div half">
                <span class="checkout-label" id="checkout-card-expiry-label">Expiry Date</span>
                <div id="checkout-card-expiry" class="checkout-stripe" aria-labelledby="checkout-card-expiry-label"></div>
              </div>
              <div class="checkout-div half">
                <span class="checkout-label" id="checkout-card-cvc-label">CVC</span>
                <div id="checkout-card-cvc" class="checkout-stripe cvc" aria-labelledby="checkout-card-cvc-label"></div>
              </div>
            </div>
          </div>
          {/if}
        {/if}
      {/if}
    </div>
  </div>
  <div slot="footer">
    <div class="flow-right">
      <button type="button" class="cancel-button" on:click|preventDefault={handleClose} disabled={isPaying}>
        Cancel
      </button>
      <button
        type="button"
        class="pay-button"
        on:click|preventDefault={handleSubmit}
        disabled={isPaying || isLoadingCards || (useNewCard && (!stripePublishableKey || !cardholderName?.trim())) || (!useNewCard && !selectedCardId)}
      >
        {#if isPaying}
          <i class="spinner loading icon"></i>
          Processing...
        {:else}
          Pay{summaryAmount ? ` ${summaryAmount}` : ' Now'}
        {/if}
      </button>
    </div>
  </div>
</Modal>

<style>
  .order-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.875rem 1rem;
    background: linear-gradient(135deg, #f0f4f8 0%, #e8edf3 100%);
    border-radius: 10px;
    margin-bottom: 1.25rem;
  }

  .order-summary-label {
    font-weight: 600;
    color: var(--grey);
    font-size: 0.9375rem;
  }

  .order-summary-amount {
    font-weight: 700;
    color: var(--primary);
    font-size: 1.125rem;
  }

  .payment-section {
    margin-top: 0;
  }

  .section-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 1rem 0;
  }

  .cards-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .payment-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .payment-option {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .payment-option:hover {
    border-color: var(--primary);
    background: #f8fafc;
  }

  .payment-option.selected {
    border-color: var(--primary);
    background: rgba(5, 108, 242, 0.05);
  }

  .payment-option input[type="radio"] {
    margin-right: 0.75rem;
    accent-color: var(--primary);
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .card-brand-icon {
    font-size: 1.5rem !important;
    color: var(--grey);
    margin: 0 !important;
  }

  .card-info {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--grey);
  }

  .new-card-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .new-card-form.has-saved-cards {
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.5rem;
  }

  .stripe-not-configured {
    padding: 1rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 8px;
    font-size: 0.9375rem;
    margin: 0;
  }

  .checkout-div {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .checkout-div.half {
    flex: 1;
  }

  .checkout-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .checkout-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--grey);
  }

  .checkout-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 1rem;
    font-family: inherit;
    background: rgb(var(--input-bg));
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .checkout-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .checkout-stripe {
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .checkout-stripe.cvc {
    max-width: 120px;
  }

  .checkout-stripe:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .flow-right {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .cancel-button {
    padding: 0.5rem 1.25rem;
    background: var(--white);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.08);
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .cancel-button:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .cancel-button:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .pay-button {
    padding: 0.5rem 1.25rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s ease;
  }

  .pay-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .pay-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .pay-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.3);
  }
</style>
